import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateTeacherInfo } from '@/hooks/useUpdateTeacherInfo';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface TeacherInfoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teacherCode: string;
  teacherName: string;
  currentBankAccount: string;
  currentBank: string;
  currentUid: string;
  currentAddress: string;
  currentIsTaxPnd3: boolean;
}

const THAI_BANKS = [
  'ไทยพาณิชย์',
  'กสิกรไทย',
  'กรุงเทพ',
  'กรุงไทย',
  'ทหารไทยธนชาต',
  'กรุงศรีอยุธยา',
  'ออมสิน',
  'ธ.ก.ส.',
  'ซีไอเอ็มบี',
  'ยูโอบี',
  'แลนด์ แอนด์ เฮ้าส์',
  'อื่นๆ',
];

export const TeacherInfoEditDialog = ({
  open,
  onOpenChange,
  teacherCode,
  teacherName,
  currentBankAccount,
  currentBank,
  currentUid,
  currentAddress,
  currentIsTaxPnd3,
}: TeacherInfoEditDialogProps) => {
  const [bankAccount, setBankAccount] = useState(currentBankAccount || '');
  const [bank, setBank] = useState(currentBank || '');
  const [uid, setUid] = useState(currentUid || '');
  const [address, setAddress] = useState(currentAddress || '');
  const [isTaxPnd3, setIsTaxPnd3] = useState(currentIsTaxPnd3);

  const updateTeacherInfo = useUpdateTeacherInfo();

  useEffect(() => {
    if (open) {
      setBankAccount(currentBankAccount || '');
      setBank(currentBank || '');
      setUid(currentUid || '');
      setAddress(currentAddress || '');
      setIsTaxPnd3(currentIsTaxPnd3);
    }
  }, [open, currentBankAccount, currentBank, currentUid, currentAddress, currentIsTaxPnd3]);

  const handleSubmit = async () => {
    await updateTeacherInfo.mutateAsync({
      teacherCode,
      bankAccount,
      bank,
      uid,
      address,
      isTaxPnd3,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>แก้ไขข้อมูลครู</DialogTitle>
          <DialogDescription>
            {teacherName} ({teacherCode})
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="uid">เลขบัตรประชาชน</Label>
            <Input
              id="uid"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              placeholder="เลขบัตรประชาชน 13 หลัก"
              maxLength={13}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="bank">ธนาคาร</Label>
            <Select value={bank} onValueChange={setBank}>
              <SelectTrigger>
                <SelectValue placeholder="เลือกธนาคาร" />
              </SelectTrigger>
              <SelectContent>
                {THAI_BANKS.map((bankName) => (
                  <SelectItem key={bankName} value={bankName}>
                    {bankName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="bankAccount">เลขบัญชี</Label>
            <Input
              id="bankAccount"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              placeholder="เลขบัญชีธนาคาร"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="address">ที่อยู่</Label>
            <Textarea
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="ที่อยู่สำหรับออกหนังสือรับรอง/ภงด."
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isTaxPnd3"
              checked={isTaxPnd3}
              onCheckedChange={(checked) => setIsTaxPnd3(checked === true)}
            />
            <Label htmlFor="isTaxPnd3">หัก ภงด.3 (3%)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={updateTeacherInfo.isPending}>
            {updateTeacherInfo.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            บันทึก
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
